import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, CheckCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { courseCategories } from "@/data/courses";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoClassPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", course_interest: "", message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.course_interest) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings/demo-class`, form);
      setSubmitted(true);
      toast.success("Demo class booked! We'll confirm your slot soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="demo-class-success">
        <section className="bg-vedanti-navy py-12 md:py-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.15)_0%,_transparent_60%)]"></div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
          <div className="container-main relative z-10">
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white">Book <span className="text-vedanti-orange">Free Demo Class</span></h1>
          </div>
        </section>
        <section className="section-padding bg-white">
          <div className="container-main max-w-lg text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-vedanti-navy mb-3">Demo Class Booked!</h2>
            <p className="text-slate-600 mb-6">We&apos;ll contact you shortly to confirm your demo class schedule. Come experience our teaching methodology first-hand!</p>
            <a href="tel:+918308776505" className="inline-flex items-center gap-2 text-vedanti-orange font-semibold text-lg">
              <Phone size={20} /> +91 8308776505
            </a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="demo-class-page">
      <Helmet>
        <title>Book a Free Demo Class | Vedanti Academy</title>
        <meta name="description" content="Experience Vedanti Academy's teaching methodology with a free demo class. Book now for Banking, SSC, MBA, Railway or Insurance exam coaching. AC classrooms, expert faculty, personalized attention." />
        <meta name="keywords" content="free demo class, coaching demo, banking coaching trial, SSC coaching trial, MBA coaching demo class Badlapur" />
        <meta property="og:title" content="Book Free Demo Class | Vedanti Academy" />
        <meta property="og:description" content="Try our coaching before you commit. Free demo classes for all competitive exam courses." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vedantiacademy.com/demo-class" />
        <link rel="canonical" href="https://vedantiacademy.com/demo-class" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Free Demo Class - Vedanti Academy",
          "description": "Experience Vedanti Academy's teaching methodology with a free demo class for Banking, SSC, MBA, Railway or Insurance exam coaching.",
          "url": "https://vedantiacademy.com/demo-class",
          "provider": {"@type": "EducationalOrganization", "name": "Vedanti Academy", "url": "https://vedantiacademy.com"},
          "serviceType": "Demo Class",
          "offers": {"@type": "Offer", "price": "0", "priceCurrency": "INR", "description": "Free demo class"}
        })}</script>
      </Helmet>
      <section className="bg-vedanti-navy py-12 md:py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.15)_0%,_transparent_60%)]"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
        <div className="container-main relative z-10">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white">Book <span className="text-vedanti-orange">Free Demo Class</span></h1>
          <p className="text-slate-300 mt-3 text-lg">Experience our teaching methodology before you commit</p>
        </div>
      </section>

      <section className="section-padding bg-white" data-testid="demo-info-section">
        <div className="container-main max-w-3xl prose prose-slate">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-vedanti-navy">Why Attend a Free Demo Class at Vedanti Academy?</h2>
          <p>A free demo class is the best way to decide whether a coaching institute is right for you before you invest your time and money. At Vedanti Academy, Badlapur, we invite every aspirant to sit in a live session with our senior faculty and experience exactly how we teach Banking (IBPS, SBI, RBI), SSC (CGL, CHSL, MTS), MBA CET, Railway (NTPC, Group&nbsp;D) and Insurance (LIC, GIC, NIACL) exams. There is no fee and no obligation to enrol.</p>
          <h3 className="font-heading text-xl font-bold text-vedanti-navy">What Happens in Your Demo Session</h3>
          <p>During the demo you will see our signature Vedic Math and reasoning shortcuts in action, understand how we break down a full exam syllabus into a realistic day-by-day study plan, and get a feel for our AC classrooms, digital boards and in-house library. Our mentors will also assess your current preparation level and suggest the most suitable batch — morning, afternoon, evening or weekend — for your goals and schedule.</p>
          <h3 className="font-heading text-xl font-bold text-vedanti-navy">The Vedanti Advantage</h3>
          <p>What makes us different is our &lsquo;Upto Selection Support&rsquo; promise: you keep attending classes until you actually clear your exam, with no fixed end date. Add to that a 360-degree hybrid model (live online + offline), daily topic-wise quizzes, sectional and full-length mock tests, and doubt-solving on WhatsApp and Zoom, and you have a complete preparation ecosystem. Our 31+ achievers placed in banks, railways, LIC and top MBA colleges — backed by 500+ 5-star Google reviews — are proof that the system works.</p>
          <p>Booking is simple. Fill in the short form above with your name, phone number and course interest, and our counselling team will call you to confirm a convenient demo slot at our Badlapur branch or online. You can also call or WhatsApp us directly at +91&nbsp;8308776505. Take the first step towards your government job or MBA dream today.</p>
        </div>
      </section>

      <section className="section-padding bg-slate-50">
        <div className="container-main max-w-2xl">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-vedanti-orange/10 rounded-xl flex items-center justify-center">
                <PlayCircle size={24} className="text-vedanti-orange" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl text-vedanti-navy">Demo Class Registration</h2>
                <p className="text-sm text-slate-500">Attend a free demo to see what makes us different</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="demo-class-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-slate-700 mb-1.5 block">Full Name *</Label>
                  <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Enter your full name" required data-testid="demo-name-input" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-slate-700 mb-1.5 block">Phone Number *</Label>
                  <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 XXXXXXXXXX" required data-testid="demo-phone-input" />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Course Interest *</Label>
                <Select onValueChange={(v) => setForm({ ...form, course_interest: v })}>
                  <SelectTrigger data-testid="demo-course-select">
                    <SelectValue placeholder="Which course are you interested in?" />
                  </SelectTrigger>
                  <SelectContent>
                    {courseCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-vedanti-orange text-white hover:bg-vedanti-orange/90 rounded-lg px-6 py-3.5 font-bold text-lg transition-all shadow-lg shadow-orange-500/20 disabled:opacity-60"
                data-testid="demo-submit-btn"
              >
                {loading ? "Submitting..." : "Book Free Demo Class"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
